//$FlowFixMe
import React, { forwardRef } from 'react'
import { useField, useFormikContext } from 'formik'
import DatePicker from 'react-datepicker'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { formInputStyle, InputForm } from 'app/assets/styles'
import { SmallText } from 'app/common/typography'
import { colors } from 'shared/assets/styles'
import styled from 'styled-components'
import { formatDateObjectToFieldDate, formatDateTime } from 'shared/utils'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePickerField.css'

const { chalk, grey, lightGrey, red } = colors

type Props = {
    name: string,
    showTimeSelect?: boolean,
    labelKey?: string,
    labelColor?: string,
    TextStyle?: any,
    minDate?: Date,
    maxDate?: Date,
    dateFilter?: Date => boolean,
    timeFilter?: Date => boolean,
    disabled?: boolean,
    error?: string,
    intl: IntlShape
}

const DatePickerFieldComponent = (props: Props) => {
    const {
        name,
        showTimeSelect,
        labelKey,
        labelColor = grey,
        TextStyle = SmallText,
        minDate,
        maxDate,
        dateFilter,
        timeFilter,
        disabled,
        error,
        intl
    } = props
    const [field] = useField(name)
    const { value } = field
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const CustomInput = forwardRef(({ date, onClick }, ref) => (
        <StyledInput
            ref={ref}
            onClick={onClick}
            error={error}
            disabled={disabled}
            value={value}>
            {value
                ? showTimeSelect
                    ? formatDateTime(value)
                    : formatDateTime(value).slice(0, 10)
                : intl.formatMessage({
                      id: showTimeSelect
                          ? 'form.datetime.placeholder'
                          : 'form.date.placeholder'
                  })}
        </StyledInput>
    ))

    const daysKeys = [
        'SUNDAY',
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY',
        'SATURDAY'
    ]
    const days = daysKeys.map(day =>
        intl.formatMessage({ id: day }).slice(0, 2)
    )
    const monthsKeys = [
        'JANUARY',
        'FEBRUARY',
        'MARCH',
        'APRIL',
        'MAY',
        'JUNE',
        'JULY',
        'AUGUST',
        'SEPTEMBER',
        'OCTOBER',
        'NOVEMBER',
        'DECEMBER'
    ]
    const months = monthsKeys.map(month => intl.formatMessage({ id: month }))

    const locale = {
        localize: {
            day: day => days[day],
            month: month => months[month]
        },
        formatLong: {
            date: () => 'mm/dd/yyyy',
            time: () => 'HH:mm'
        }
    }

    return (
        <InputForm>
            {labelKey && (
                <FormattedMessage id={labelKey}>
                    {(text: string) => (
                        <TextStyle color={labelColor}>{text}</TextStyle>
                    )}
                </FormattedMessage>
            )}
            <DatePicker
                showTimeSelect={showTimeSelect}
                selected={(value && new Date(value)) || null}
                onChange={date => {
                    setFieldValue(name, formatDateObjectToFieldDate(date))
                }}
                customInput={<CustomInput />}
                locale={locale}
                timeIntervals={15}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={dateFilter}
                filterTime={timeFilter}
                calendarStartDay={1}
                disabled={disabled}
                onChangeRaw={e => {
                    setFieldTouched(name, true, true)
                }}
            />
        </InputForm>
    )
}

const StyledInput = styled.div`
    ${formInputStyle};
    color: ${props => (props.value ? 'black' : lightGrey)};
    height: 40px;
    display: flex;
    align-items: center;
    border-color: ${props => props.error && red};
    background-color: ${props => props.disabled && chalk};
`

const DatePickerField = injectIntl(DatePickerFieldComponent)
export { DatePickerField }
